import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useMemo, useState } from 'react';
import useFetch from 'use-http';
import { ILeaseMasterSearch } from 'src/models/dlcLease/ILeaseMasterSearch';
import Page from '../common/Page';
import { RouteComponentProps } from 'react-router-dom';
import { Table, ITableConfig, ITableColumn } from '../common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Card from '../common/Card.redesign';
import CardContainer from '../common/CardContainer.redesign';
import * as styles from './sections/styles';
import './SearchLeases.scss';
import FindCheck from '../invoicing/components/FindCheck';
import ARInvoiceHistoryReportDialog from './sections/ARInvoiceHistoryReportDialog';
import LeaseholdARReportsDialog from './sections/LeaseholdARReportsDialog';
import LeaseholdPayableReconciliationDialog from './sections/LeaseholdPayableReconciliationDialog';
import CreateManualLeaseModal from './CreateManualLeaseModal';
import DraftsDueReportDialog from './sections/DraftsDueReportDialog';
import LeaseInvoiceReportDialog from './sections/LeaseInvoiceReportDialog';
import InvoiceGroupLookup from './sections/InvoiceGroupLookup';
import useLatestRequest from './useLatestRequest';

const PAGE_SIZE = 25;
const LEASE_SEARCH_STORAGE_KEY = 'landhawk.leases.searchState.v1';

interface ISortConfig {
    field: string | null;
    isDescending: boolean | null;
}

interface ISortableTableColumn extends ITableColumn {
    sortable?: boolean;
}

interface ILeaseKind {
    code: string;
    dscr: string;
}

interface ILeaseSearchFilters {
    leaseNumber: string;
    lessee: string;
    projectNumber: string;
    prospectNumber: string;
    lessor: string;
    invoiceNo: string;
    clientLease: string;
    comments: string;
}

interface ILeaseSearchStorage {
    filters: ILeaseSearchFilters;
    pageNumber: number;
    sortConfig: ISortConfig;
}

const defaultFilters: ILeaseSearchFilters = {
    leaseNumber: '',
    lessee: '',
    projectNumber: '',
    prospectNumber: '',
    lessor: '',
    invoiceNo: '',
    clientLease: '',
    comments: '',
};

const defaultSortConfig: ISortConfig = { field: 'lsNo', isDescending: false };

const getStoredLeaseSearchState = (): ILeaseSearchStorage | null => {
    try {
        const storedValue = window.localStorage.getItem(LEASE_SEARCH_STORAGE_KEY);
        if (!storedValue) {
            return null;
        }

        const parsed = JSON.parse(storedValue) as ILeaseSearchStorage;
        return {
            filters: { ...defaultFilters, ...(parsed.filters || {}) },
            pageNumber: parsed.pageNumber && parsed.pageNumber > 0 ? parsed.pageNumber : 1,
            sortConfig: parsed.sortConfig || defaultSortConfig,
        };
    } catch {
        return null;
    }
};

const setStoredLeaseSearchState = (state: ILeaseSearchStorage) => {
    try {
        window.localStorage.setItem(LEASE_SEARCH_STORAGE_KEY, JSON.stringify(state));
    } catch {
        // Storage can be unavailable in private browsing or locked-down browsers.
    }
};

const SearchLeases: React.FC<RouteComponentProps> = (props) => {
    // --- State Management ---
    const { get: getLookups, response: lookupResponse } = useFetch();
    const { get: getLeases, response: leasesResponse } = useFetch();
    const lookupRequests = useLatestRequest();
    const leaseRequests = useLatestRequest();
    const [storedSearchState] = useState<ILeaseSearchStorage | null>(() => getStoredLeaseSearchState());
    const [leases, setLeases] = useState<PagedResultSet<ILeaseMasterSearch> | undefined>();
    const [leaseKinds, setLeaseKinds] = useState<Map<string, string>>(new Map());
    const [leaseKindOptions, setLeaseKindOptions] = useState<ILeaseKind[]>([]);
    const [isCreateManualOpen, setIsCreateManualOpen] = useState(false);
    const [pageNumber, setPageNumber] = useState(storedSearchState?.pageNumber || 1);
    const [loading, setLoading] = useState(false);
    const [shouldReload, setShouldReload] = useState(true);
    const [sortConfig, setSortConfig] = useState<ISortConfig>(storedSearchState?.sortConfig || defaultSortConfig);
    const [isARHistoryReportDialogOpen, setIsARHistoryReportDialogOpen] = useState(false);
    const [isLeaseholdARReportsDialogOpen, setIsLeaseholdARReportsDialogOpen] = useState(false);
    const [isLeaseholdPayableReconciliationDialogOpen, setIsLeaseholdPayableReconciliationDialogOpen] = useState(false);
    const [isDraftsDueReportDialogOpen, setIsDraftsDueReportDialogOpen] = useState(false);
    const [isLeaseInvoiceReportDialogOpen, setIsLeaseInvoiceReportDialogOpen] = useState(false);
    const [draftsDueReportFormat, setDraftsDueReportFormat] = useState<'EXCELOPENXML' | 'PDF'>('EXCELOPENXML');

    // --- Search Filter State ---
    const [leaseNumberFilter, setLeaseNumberFilter] = useState(storedSearchState?.filters.leaseNumber || '');
    const [lesseeFilter, setLesseeFilter] = useState(storedSearchState?.filters.lessee || '');
    const [projectNumberFilter, setProjectNumberFilter] = useState(storedSearchState?.filters.projectNumber || '');
    const [prospectNumberFilter, setProspectNumberFilter] = useState(storedSearchState?.filters.prospectNumber || '');

    // NEW Filter States
    const [lessorFilter, setLessorFilter] = useState(storedSearchState?.filters.lessor || '');
    const [invoiceNoFilter, setInvoiceNoFilter] = useState(storedSearchState?.filters.invoiceNo || '');
    const [clientLeaseFilter, setClientLeaseFilter] = useState(storedSearchState?.filters.clientLease || '');
    const [commentsFilter, setCommentsFilter] = useState(storedSearchState?.filters.comments || '');

    const currentFilters: ILeaseSearchFilters = useMemo(
        () => ({
            leaseNumber: leaseNumberFilter,
            lessee: lesseeFilter,
            projectNumber: projectNumberFilter,
            prospectNumber: prospectNumberFilter,
            lessor: lessorFilter,
            invoiceNo: invoiceNoFilter,
            clientLease: clientLeaseFilter,
            comments: commentsFilter,
        }),
        [leaseNumberFilter, lesseeFilter, projectNumberFilter, prospectNumberFilter, lessorFilter, invoiceNoFilter, clientLeaseFilter, commentsFilter]
    );

    // --- Effect for Initial Lookup Data Loading ---
    useEffect(() => {
        const loadInitialLookups = async () => {
            const requestId = lookupRequests.begin();
            const kindsData: ILeaseKind[] = await getLookups('/v1.0/leases/master/lease-kinds');
            if (lookupRequests.isCurrent(requestId) && lookupResponse.ok && Array.isArray(kindsData)) {
                setLeaseKindOptions(kindsData);
                const kindsMap = new Map(kindsData.map((kind) => [kind.code, kind.dscr]));
                setLeaseKinds(kindsMap);
            }
        };
        loadInitialLookups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Effect for Data Loading ---
    useEffect(() => {
        if (shouldReload) {
            loadLeases();
            setShouldReload(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, shouldReload, sortConfig]);

    useEffect(() => {
        setStoredLeaseSearchState({
            filters: currentFilters,
            pageNumber,
            sortConfig,
        });
    }, [currentFilters, pageNumber, sortConfig]);

    // --- Data Fetching ---
    const loadLeases = async () => {
        setLoading(true);
        const skip = (pageNumber - 1) * PAGE_SIZE;

        const params = new URLSearchParams();
        params.append('skip', skip.toString());
        params.append('top', PAGE_SIZE.toString());

        const filters: { Field: string; Value: string }[] = [];

        if (leaseNumberFilter.trim()) {
            const sanitizedLeaseNumber = leaseNumberFilter.trim().replace(/-/g, '');
            filters.push({ Field: 'lsNo', Value: sanitizedLeaseNumber });
        }
        if (lesseeFilter.trim()) {
            filters.push({ Field: 'lessee', Value: lesseeFilter.trim() });
        }
        if (projectNumberFilter.trim()) {
            filters.push({ Field: 'clNo', Value: projectNumberFilter.trim() });
        }
        if (prospectNumberFilter.trim()) {
            filters.push({ Field: 'prNo', Value: prospectNumberFilter.trim() });
        }

        // --- NEW Filters Append Logic ---
        if (lessorFilter.trim()) {
            filters.push({ Field: 'lessor', Value: lessorFilter.trim() });
        }
        if (invoiceNoFilter.trim()) {
            filters.push({ Field: 'invoiceNo', Value: invoiceNoFilter.trim() });
        }
        if (clientLeaseFilter.trim()) {
            filters.push({ Field: 'clientLease', Value: clientLeaseFilter.trim() });
        }
        if (commentsFilter.trim()) {
            filters.push({ Field: 'comments', Value: commentsFilter.trim() });
        }

        filters.forEach((filter, index) => {
            params.append(`Filters[${index}].Field`, filter.Field);
            params.append(`Filters[${index}].Value`, filter.Value);
            params.append(`Filters[${index}].Operation`, 'Contains');
        });

        if (sortConfig.field && sortConfig.isDescending !== null) {
            params.append('OrderBy.Field', sortConfig.field);
            params.append('OrderBy.IsDescending', String(sortConfig.isDescending));
        }

        const requestId = leaseRequests.begin();
        const data = await getLeases(`/v1.0/leases/master/search?${params.toString()}`);

        if (leaseRequests.isCurrent(requestId) && leasesResponse.ok) {
            const pagedResult = new PagedResultSet<ILeaseMasterSearch>();
            pagedResult.items = data.items;
            pagedResult.totalRecords = data.totalCount;
            pagedResult.pageNumber = pageNumber;
            pagedResult.pageSize = PAGE_SIZE;
            setLeases(pagedResult);
        }

        if (leaseRequests.isCurrent(requestId)) {
            setLoading(false);
        }
    };

    // --- Event Handlers ---
    const handleSort = (field: string) => {
        setSortConfig((currentConfig) => {
            if (currentConfig.field !== field) {
                return { field: field, isDescending: false };
            }
            if (currentConfig.isDescending === false) {
                return { field: field, isDescending: true };
            }
            return { field: null, isDescending: null };
        });
        setPageNumber(1);
        setShouldReload(true);
    };

    const handleSearch = () => {
        setPageNumber(1);
        setShouldReload(true);
    };

    const handleClearFilters = () => {
        setLeaseNumberFilter('');
        setLesseeFilter('');
        setProjectNumberFilter('');
        setProspectNumberFilter('');
        setLessorFilter('');
        setInvoiceNoFilter('');
        setClientLeaseFilter('');
        setCommentsFilter('');

        setPageNumber(1);
        setSortConfig(defaultSortConfig);
        setShouldReload(true);
    };

    const hasActiveFilters =
        leaseNumberFilter !== '' ||
        lesseeFilter !== '' ||
        projectNumberFilter !== '' ||
        prospectNumberFilter !== '' ||
        lessorFilter !== '' ||
        invoiceNoFilter !== '' ||
        clientLeaseFilter !== '' ||
        commentsFilter !== '';

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleRowClick = (id: number | string) => {
        props.history.push(`/leases/${id}`);
    };

    const handlePageChange = (newPageNumber: number) => {
        setPageNumber(newPageNumber);
        setShouldReload(true);
    };

    // --- Formatters ---
    const formatDate = (dateStr: string): string => {
        if (!dateStr) {
            return '-';
        }
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
            return '-';
        }
        return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
    };

    const formatKind = (code: string): string => {
        if (!code) {
            return '-';
        }
        return leaseKinds.get(code) || code;
    };

    const formatCurrency = (value: string): string => {
        const num = parseFloat(value);
        if (isNaN(num)) {
            return '-';
        }
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(num);
    };

    const formatNumber = (value: string): string => {
        const num = parseFloat(value);
        if (isNaN(num)) {
            return '-';
        }
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 8 }).format(num);
    };

    const formatBoolean = (value: string): string => {
        return value === 'true' ? 'Yes' : 'No';
    };
    const formatStatus = (value: string): string => {
        return value || 'Active';
    };

    // --- Table Configuration ---
    const columns: ISortableTableColumn[] = [
        { name: 'Lease #', mapping: 'lsNo', sortable: true },
        { name: 'Source', mapping: 'source' },
        { name: 'Kind', mapping: 'kind', format: formatKind },
        { name: 'Lessee', mapping: 'lessee', sortable: true },
        { name: 'Lessor', mapping: 'lessor' },
        { name: 'Client #', mapping: 'clNo', sortable: true },
        { name: 'Project #', mapping: 'prNo', sortable: true },
        { name: 'Lease Date', mapping: 'leaseDate', format: formatDate, sortable: true },
        { name: 'Effective Date', mapping: 'effectiveDate', format: formatDate, sortable: true },
        { name: 'Expiration Date', mapping: 'expirationDate', format: formatDate, sortable: true },
        { name: 'Created Date', mapping: 'createDt', format: formatDate, sortable: true },
        { name: 'Gross Acres', mapping: 'grossAcres', format: formatNumber },
        { name: 'Net Acres', mapping: 'netAcres', format: formatNumber },
        { name: 'Bonus/Acre', mapping: 'bonusPerAcre', format: formatCurrency },
        { name: 'Status', mapping: 'accountStatus', format: formatStatus },
        { name: 'Void', mapping: 'isVoid', format: formatBoolean },
    ];

    const tableConfig: ITableConfig = {
        rowCount: PAGE_SIZE,
        pageNumber: pageNumber,
        idField: 'lsNo',
        columns: columns,
    };

    const reportActions = [
        {
            label: 'A/R Invoice History',
            onClick: () => setIsARHistoryReportDialogOpen(true),
        },
        {
            label: 'Leasehold A/R Reports',
            onClick: () => setIsLeaseholdARReportsDialogOpen(true),
        },
        {
            label: 'Drafts Due (.xlsx)',
            onClick: () => {
                setDraftsDueReportFormat('EXCELOPENXML');
                setIsDraftsDueReportDialogOpen(true);
            },
        },
        {
            label: 'Drafts Due (.pdf)',
            onClick: () => {
                setDraftsDueReportFormat('PDF');
                setIsDraftsDueReportDialogOpen(true);
            },
        },
        {
            label: 'Lease Invoice',
            onClick: () => setIsLeaseInvoiceReportDialogOpen(true),
        },
        {
            label: 'Leasehold Payable Reconciliation Utility',
            onClick: () => setIsLeaseholdPayableReconciliationDialogOpen(true),
        },
    ];

    const compactPrimaryButtonStyle: React.CSSProperties = {
        ...styles.buttonPrimary,
        height: '34px',
        padding: '0 0.9rem',
        fontSize: styles.typography.fontSize.xs,
        fontWeight: styles.typography.fontWeight.semibold,
        whiteSpace: 'nowrap',
    };

    const compactReportButtonStyle: React.CSSProperties = {
        ...styles.buttonSecondary,
        height: '30px',
        padding: '0 0.7rem',
        fontSize: '0.95rem',
        fontWeight: styles.typography.fontWeight.semibold,
        whiteSpace: 'nowrap',
        border: `1px solid ${styles.colors.gray300}`,
    };

    const compactSecondaryButtonStyle: React.CSSProperties = {
        ...styles.buttonSecondary,
        height: '34px',
        padding: '0 0.9rem',
        fontSize: styles.typography.fontSize.xs,
        fontWeight: styles.typography.fontWeight.semibold,
        whiteSpace: 'nowrap',
    };

    // --- JSX Render ---
    return (
        <Page>
            <Page.Section width={12}>
                <CardContainer className="search-leases-layout">
                    <Card className="search-leases search-leases__reports-card" collapse={true}>
                        <div className="search-leases__reports-bar">
                            <div className="search-leases__reports-actions">
                                {reportActions.map((action) => (
                                    <button
                                        key={action.label}
                                        onClick={action.onClick}
                                        className="search-leases__report-button"
                                        style={compactReportButtonStyle}
                                    >
                                        {action.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Card>
                    <Card className="search-leases search-leases__main-card" collapse={true}>
                        <div className="search-leases__top">
                            <div className="search-leases__toolbar">
                                <button
                                    onClick={() => setIsCreateManualOpen(true)}
                                    className="search-leases__action-button"
                                    style={compactPrimaryButtonStyle}
                                >
                                    New Manual Lease Record
                                </button>
                                <div className="search-leases__toolbar-actions">
                                    <div className="search-leases__direct-lookups">
                                        <InvoiceGroupLookup />
                                        <FindCheck />
                                    </div>
                                </div>
                            </div>

                            <div className="search-leases__search-bar">
                                <div className="search-leases__toolbar-title-wrap">
                                    <h1 className="search-leases__toolbar-title">Lease Search</h1>
                                    <p className="search-leases__toolbar-subtitle">
                                        {leases ? `${leases.totalRecords} total leases` : 'Search and open lease records'}
                                    </p>
                                </div>

                                {/* Filter Grid */}
                                <div className="search-leases__filter-grid">
                                    <LeaseRecordInput
                                        type="text"
                                        placeholder="Lease Number..."
                                        value={leaseNumberFilter}
                                        onChange={(e) => setLeaseNumberFilter(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="search-leases__filter-input"
                                        style={styles.inputBase}
                                    />
                                    <LeaseRecordInput
                                        type="text"
                                        placeholder="Lessee Name..."
                                        value={lesseeFilter}
                                        onChange={(e) => setLesseeFilter(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="search-leases__filter-input"
                                        style={styles.inputBase}
                                    />
                                    <LeaseRecordInput
                                        type="text"
                                        placeholder="Lessor Name..."
                                        value={lessorFilter}
                                        onChange={(e) => setLessorFilter(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="search-leases__filter-input"
                                        style={styles.inputBase}
                                    />
                                    <LeaseRecordInput
                                        type="text"
                                        placeholder="Client Lease Number..."
                                        value={clientLeaseFilter}
                                        onChange={(e) => setClientLeaseFilter(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="search-leases__filter-input"
                                        style={styles.inputBase}
                                    />
                                    <LeaseRecordInput
                                        type="text"
                                        placeholder="Client Number..."
                                        value={projectNumberFilter}
                                        onChange={(e) => setProjectNumberFilter(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="search-leases__filter-input"
                                        style={styles.inputBase}
                                    />
                                    <LeaseRecordInput
                                        type="text"
                                        placeholder="Project Number..."
                                        value={prospectNumberFilter}
                                        onChange={(e) => setProspectNumberFilter(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="search-leases__filter-input"
                                        style={styles.inputBase}
                                    />
                                    <LeaseRecordInput
                                        type="text"
                                        placeholder="Invoice Number..."
                                        value={invoiceNoFilter}
                                        onChange={(e) => setInvoiceNoFilter(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="search-leases__filter-input"
                                        style={styles.inputBase}
                                    />
                                    <LeaseRecordInput
                                        type="text"
                                        placeholder="Invoice Comments..."
                                        value={commentsFilter}
                                        onChange={(e) => setCommentsFilter(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        className="search-leases__filter-input"
                                        style={styles.inputBase}
                                    />
                                </div>
                                <div className="search-leases__search-actions">
                                    <button
                                        onClick={handleSearch}
                                        disabled={loading}
                                        className="search-leases__action-button"
                                        style={compactPrimaryButtonStyle}
                                    >
                                        {loading ? 'Searching...' : 'Search'}
                                    </button>
                                    {hasActiveFilters && (
                                        <button
                                            onClick={handleClearFilters}
                                            className="search-leases__action-button"
                                            style={compactSecondaryButtonStyle}
                                        >
                                            Clear Filters
                                        </button>
                                    )}
                                </div>
                            </div>

                            {leases && leases.items.length === 0 && !loading && (
                                <div className="search-leases__empty-state">
                                    <h3>No leases found</h3>
                                    <p>Try adjusting your search filters to find what you&apos;re looking for.</p>
                                </div>
                            )}
                        </div>

                        <div className="search-leases__table-section">
                            <div className="search-leases__table-wrapper">
                                <Table
                                    isLoading={loading}
                                    fixedPager={true}
                                    tableConfig={tableConfig}
                                    data={leases}
                                    onRowClick={handleRowClick}
                                    enablePaging={true}
                                    onPageChange={handlePageChange}
                                    onSort={handleSort}
                                    sortConfig={sortConfig}
                                />
                            </div>
                        </div>
                    </Card>
                </CardContainer>
                <ARInvoiceHistoryReportDialog isOpen={isARHistoryReportDialogOpen} onClose={() => setIsARHistoryReportDialogOpen(false)} />
                <LeaseholdARReportsDialog isOpen={isLeaseholdARReportsDialogOpen} onClose={() => setIsLeaseholdARReportsDialogOpen(false)} />
                <LeaseholdPayableReconciliationDialog
                    isOpen={isLeaseholdPayableReconciliationDialogOpen}
                    onClose={() => setIsLeaseholdPayableReconciliationDialogOpen(false)}
                />
                <CreateManualLeaseModal
                    isOpen={isCreateManualOpen}
                    leaseKinds={leaseKindOptions}
                    onClose={() => setIsCreateManualOpen(false)}
                    onCreated={(leaseNumber) => props.history.push(`/leases/${leaseNumber}`)}
                />
                <DraftsDueReportDialog
                    isOpen={isDraftsDueReportDialogOpen}
                    format={draftsDueReportFormat}
                    onClose={() => setIsDraftsDueReportDialogOpen(false)}
                />
                <LeaseInvoiceReportDialog isOpen={isLeaseInvoiceReportDialogOpen} onClose={() => setIsLeaseInvoiceReportDialogOpen(false)} />
            </Page.Section>
        </Page>
    );
};

export default SearchLeases;



// WEBPACK FOOTER //
// ./src/components/leases/SearchLeases.tsx